if (!customElements.get('size-table')) {
class SizeTable extends HTMLElement {
  constructor() {
    super();
    this.tableContainer = this.querySelector('.size-table__body');
    this.tableType = this.dataset.type || (this.classList.contains('size-table--measurements') ? 'measurements' : 'conversions');
    this.labels = {
      uk: this.dataset.labelUk || '',
      usa: this.dataset.labelUsa || '',
      eu1: this.dataset.labelEu1 || '',
      eu1Countries: this.dataset.labelEu1Countries || '',
      eu2: this.dataset.labelEu2 || '',
      eu2Countries: this.dataset.labelEu2Countries || '',
      italy: this.dataset.labelItaly || '',
      ukSize: this.dataset.labelUkSize || '',
    };

    if (this.tableContainer?.dataset.table) {
      this.createTable(this.tableContainer.dataset.table);
    }
  }

  async createTable(url) {
    try {
      const parsedTable = await this.downloadAndParseData(url);
      const rows = this.normalizeRows(parsedTable.data || []);
      if (!rows.length) {
        this.tableContainer.innerHTML = '';
        return;
      }

      if (this.tableType === 'measurements') {
        this.tableContainer.innerHTML = this.buildMeasurementsTable(rows);
      } else {
        this.tableContainer.innerHTML = this.buildConversionTable(rows);
      }
    } catch (error) {
      throw new Error(error);
    }
  }

  downloadAndParseData(url) {
    return new Promise((resolve, reject) => {
      Papa.parse(url, {
        download: true,
        complete: (results) => {
          resolve(results);
        },
        error: (error) => {
          reject(error);
        },
      });
    });
  }

  normalizeRows(rows) {
    return rows
      .filter((row) => Array.isArray(row) && row.some((cell) => `${cell ?? ''}`.trim() !== ''))
      .map((row) => row.map((cell) => `${cell ?? ''}`.trim()));
  }

  buildConversionTable(rows) {
    const [headerRow, ...valueRows] = rows;
    if (!headerRow || headerRow.length < 2 || !valueRows.length) return '';

    const regionRows = headerRow.slice(1).map((regionLabel, regionIndex) => {
      const values = valueRows.map((row) => row[regionIndex + 1] || '');
      return `
        <tr class="size-table__table-row">
          <th scope="row" class="size-table__table-header-cell size-table__table-header-cell--row">
            ${this.formatConversionLabel(regionLabel)}
          </th>
          ${values
            .map(
              (value) => `
                <td class="size-table__table-cell body-1">
                  ${this.escapeHtml(value)}
                </td>
              `
            )
            .join('')}
        </tr>
      `;
    });

    return `
      <div class="size-table__table-wrapper">
        <table class="size-table__table">
          <tbody class="size-table__table-body">
            ${regionRows.join('')}
          </tbody>
        </table>
      </div>
    `;
  }

  buildMeasurementsTable(rows) {
    const [headerRow, ...bodyRows] = rows;
    if (!headerRow || !bodyRows.length) return '';

    const normalizedHeaders = [...headerRow];
    if (normalizedHeaders[0]) {
      normalizedHeaders[0] = this.labels.ukSize || normalizedHeaders[0];
    }

    return `
      <div class="size-table__table-wrapper">
        <table class="size-table__table">
          <thead>
            <tr class="size-table__table-header-row">
              ${normalizedHeaders
                .map(
                  (header) => `
                    <th class="size-table__table-header-cell" scope="col">
                      ${this.escapeHtml(header)}
                    </th>
                  `
                )
                .join('')}
            </tr>
          </thead>
          <tbody class="size-table__table-body">
            ${bodyRows
              .map(
                (row) => `
                  <tr class="size-table__table-row">
                    ${row
                      .map(
                        (cell, index) => `
          <td class="size-table__table-cell body-1${index === 0 ? ' size-table__table-cell--size' : ''}">
            ${this.formatMeasurementCell(cell, index)}
          </td>
        `
                      )
                      .join('')}
                  </tr>
                `
              )
              .join('')}
          </tbody>
        </table>
      </div>
    `;
  }

  formatConversionLabel(regionLabel) {
    const normalizedLabel = regionLabel.toUpperCase();
    switch (normalizedLabel) {
      case 'UK-AU':
        return this.escapeHtml(this.labels.uk || regionLabel);
      case 'US':
        return this.escapeHtml(this.labels.usa || regionLabel);
      case 'EUR1':
        return `
          ${this.escapeHtml(this.labels.eu1 || regionLabel)}
          ${this.labels.eu1Countries ? `<span class="size-table__row-label-sub">${this.escapeHtml(this.labels.eu1Countries)}</span>` : ''}
        `;
      case 'EUR2':
        return `
          ${this.escapeHtml(this.labels.eu2 || regionLabel)}
          ${this.labels.eu2Countries ? `<span class="size-table__row-label-sub">${this.escapeHtml(this.labels.eu2Countries)}</span>` : ''}
        `;
      case 'IT':
        return this.escapeHtml(this.labels.italy || regionLabel);
      default:
        return this.escapeHtml(regionLabel);
    }
  }

  formatMeasurementCell(value, index) {
    if (index === 0 || !value.includes('|')) {
      return this.escapeHtml(value);
    }

    const [cmValue, inchValue] = value.split('|');
    return `
      <span class="cm">${this.escapeHtml(cmValue)}</span>
      <span class="inch">${this.escapeHtml(inchValue)}</span>
    `;
  }

  setMeasurementsUnit(unit = 'in') {
    this.dataset.measurements = unit === 'cm' ? 'cm' : 'inch';
  }

  escapeHtml(value) {
    return `${value}`
      .replaceAll('&', '&amp;')
      .replaceAll('<', '&lt;')
      .replaceAll('>', '&gt;')
      .replaceAll('"', '&quot;')
      .replaceAll("'", '&#39;');
  }
}

customElements.define('size-table', SizeTable);
}